/**
 * Which script a shul's public site says each kind of text in.
 *
 * The data already carries both scripts — an occasion's `labelEn`/`labelHe`, a
 * day's two headings, a zman's two catalog names, a civil date and a Hebrew
 * one — so the setting CHOOSES between them and translates nothing. A group set
 * to one script shows that script alone; only `both` sets the two side by side.
 *
 * Not every text is a choice: the rav's line and the shul's greeting are the
 * shul's own words and show as written, a time is a figure, and the printed
 * sheet keeps its own Hebrew.
 */
export const PUBLIC_SCRIPTS = ['en', 'he', 'both'] as const

export type PublicScript = (typeof PUBLIC_SCRIPTS)[number]

/**
 * The kinds of text a shul decides for, each a key of the stored setting.
 *
 * - `name` — the shul's own name: the masthead, the footer, the name heading
 *   its details on the home page.
 * - `occasions` — occasion and day names: an occasion's name, a day heading,
 *   the schedule's tabs and title.
 * - `lines` — tefillos and schedule lines: Shachris, Mincha, candle lighting,
 *   a shiur, shkia where a day's lines place it.
 * - `zmanim` — the names on the home page's line through today.
 * - `dates` — `en` is the civil date and `he` the Hebrew date, wherever a date
 *   is stated.
 */
export const PUBLIC_TEXT_GROUPS = [
  'name',
  'occasions',
  'lines',
  'zmanim',
  'dates',
] as const

export type PublicTextGroup = (typeof PUBLIC_TEXT_GROUPS)[number]

export type PublicLanguage = Record<PublicTextGroup, PublicScript>

/**
 * English throughout, and the civil date: a visitor reads a site in one
 * language unless the shul says otherwise.
 */
export const DEFAULT_PUBLIC_LANGUAGE: PublicLanguage = {
  name: 'en',
  occasions: 'en',
  lines: 'en',
  zmanim: 'en',
  dates: 'en',
}

const isScript = (v: unknown): v is PublicScript =>
  (PUBLIC_SCRIPTS as readonly unknown[]).includes(v)

/**
 * The stored setting as a whole answer: each group it names with a known
 * script keeps it, and every other group takes its default. So a group added
 * later reads as its default for every shul that has not chosen, with no
 * backfill.
 */
export function publicLanguageOf(stored: unknown): PublicLanguage {
  const given =
    stored && typeof stored === 'object'
      ? (stored as Record<string, unknown>)
      : {}
  return Object.fromEntries(
    PUBLIC_TEXT_GROUPS.map((g) => [
      g,
      isScript(given[g]) ? given[g] : DEFAULT_PUBLIC_LANGUAGE[g],
    ]),
  ) as PublicLanguage
}

/** What one piece of text shows: its English, its Hebrew, or both. */
export type Said = { en: string | null; he: string | null }

/**
 * The script a group chose, applied to one text's two forms.
 *
 * A form that is missing is never invented: where the chosen script has
 * nothing — a shul with no English name, a Yom Tov with no Hebrew label — the
 * other one stands in, so a name is never blank because of a setting.
 */
export function say(
  script: PublicScript,
  en: string | null | undefined,
  he: string | null | undefined,
): Said {
  const e = en?.trim() ? en : null
  const h = he?.trim() ? he : null
  if (script === 'both') return { en: e, he: h }
  if (script === 'he') return h ? { en: null, he: h } : { en: e, he: null }
  return e ? { en: e, he: null } : { en: null, he: h }
}

/** What the settings page offers, one choice per group. */
export const PUBLIC_LANGUAGE_OPTIONS: {
  group: PublicTextGroup
  label: string
  options: { value: PublicScript; label: string }[]
}[] = [
  {
    group: 'name',
    label: 'The shul’s name',
    options: [
      { value: 'en', label: 'English' },
      { value: 'he', label: 'Hebrew' },
      { value: 'both', label: 'Both' },
    ],
  },
  {
    group: 'occasions',
    label: 'Occasion and day names',
    options: [
      { value: 'en', label: 'English' },
      { value: 'he', label: 'Hebrew' },
      { value: 'both', label: 'Both' },
    ],
  },
  {
    group: 'lines',
    label: 'Tefillos and schedule lines',
    options: [
      { value: 'en', label: 'English' },
      { value: 'he', label: 'Hebrew' },
      { value: 'both', label: 'Both' },
    ],
  },
  {
    group: 'zmanim',
    label: 'Zmanim names',
    options: [
      { value: 'en', label: 'English' },
      { value: 'he', label: 'Hebrew' },
      { value: 'both', label: 'Both' },
    ],
  },
  {
    group: 'dates',
    label: 'Dates',
    options: [
      { value: 'en', label: 'Civil' },
      { value: 'he', label: 'Hebrew' },
      { value: 'both', label: 'Both' },
    ],
  },
]
